<template>
  <div class="background bg-dark">
    <h3 class="title">Smart Listing ↔ Table Converter</h3>

    <div class="background">
      <div class="container">
        <div class="columns">
          <!-- Input -->
          <div class="column">
            <h4>Input</h4>
            <textarea
              v-model="inputText"
              placeholder="📌 INSTRUCTIONS
  
  1️⃣ Paste KEY: VALUE format
  
  Example:
  first: Red
  second: Banana
  third: Yellow
  
  (Use blank line to separate multiple rows)
  
  2️⃣ OR paste a MARKDOWN TABLE
  
  | first | second | third |
  | Red   | Banana | Yellow |
  
  ➡ Auto converts while typing"
            ></textarea>

            <div class="buttons">
              <button @click="autoConvert">Auto Convert</button>
              <button @click="copyTSV">Copy for Excel</button>
            </div>
          </div>

          <!-- Output -->
          <div class="column">
            <h4>Output</h4>
            <textarea v-model="outputText"></textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

const inputText = ref("");
const outputText = ref("");

/* ---------- AUTO LIVE CONVERT ---------- */
watch(inputText, (val) => {
  if (!val.trim()) {
    outputText.value = "";
    return;
  }

  // Markdown table
  if (val.includes("|")) {
    tableToKeyValue();
    return;
  }

  // Excel / Sheets TSV
  if (val.includes("\t")) {
    tsvToKeyValue();
    return;
  }

  // key:value listing
  if (val.includes(":")) {
    keyValueToTable();
  }
});

/* ---------- Manual trigger ---------- */
const autoConvert = () => {
  if (inputText.value.includes("|")) {
    tableToKeyValue();
  } else if (inputText.value.includes(":")) {
    keyValueToTable();
  }
};

/* ---------- key:value → table (multi-row) ---------- */
const keyValueToTable = () => {
  const blocks = inputText.value.split(/\n\s*\n/);
  const rows = [];
  let headers = [];

  blocks.forEach((block) => {
    const lines = block.split("\n").filter((l) => l.includes(":"));
    if (!lines.length) return;

    const row = {};
    lines.forEach((line) => {
      const [k, ...v] = line.split(":");
      row[k.trim()] = v.join(":").trim();
    });

    headers = [...new Set([...headers, ...Object.keys(row)])];
    rows.push(row);
  });

  let table = "| " + headers.join(" | ") + " |\n";
  table += "| " + headers.map(() => "---").join(" | ") + " |\n";

  rows.forEach((row) => {
    table += "| " + headers.map((h) => row[h] || "").join(" | ") + " |\n";
  });

  outputText.value = table.trim();
};

/* ---------- table → key:value ---------- */
const tableToKeyValue = () => {
  const rawLines = inputText.value
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("|") && l.endsWith("|"));

  if (rawLines.length < 2) return;

  // headers = first row
  const headers = rawLines[0]
    .split("|")
    .map((c) => c.trim())
    .filter(Boolean);

  // remaining rows (skip separator row if exists)
  const dataRows = rawLines.slice(1).filter(
    (row) =>
      !row
        .replace(/\|/g, "")
        .trim()
        .match(/^[-\s]+$/)
  );

  const result = [];

  dataRows.forEach((row) => {
    const values = row
      .split("|")
      .map((c) => c.trim())
      .filter(Boolean);

    headers.forEach((h, i) => {
      result.push(`${h}: ${values[i] || ""}`);
    });

    result.push("");
  });

  outputText.value = result.join("\n").trim();
};

/* ---------- Copy TSV for Excel ---------- */
const copyTSV = async () => {
  const tsv = outputText.value.replace(/\|/g, "\t").replace(/---/g, "").trim();

  await navigator.clipboard.writeText(tsv);
  alert("Copied for Excel / Google Sheets");
};

const tsvToKeyValue = () => {
  const lines = inputText.value
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) return;

  const headers = lines[0].split("\t");

  const result = [];

  lines.slice(1).forEach((line) => {
    const values = line.split("\t");
    headers.forEach((h, i) => {
      result.push(`${h}: ${values[i] || ""}`);
    });
    result.push("");
  });

  outputText.value = result.join("\n").trim();
};
</script>

<style scoped>
/* --------- DARK THEME (BRIGHTER TEXT) --------- */
.title {
  text-align: center;
  padding: 20px 0;
  color: #f8fafc; /* brighter */
  font-weight: 700;
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 20px;
}

.columns {
  display: flex;
  gap: 20px;
}

.column {
  flex: 1;
  /* background: #020617; */
  /* border: 1px solid #475569; */
  border-radius: 12px;
  padding: 16px;
}

h4 {
  color: #e5e7eb;
  margin-bottom: 8px;
}

textarea {
  min-height: 360px;
  width: 100%;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 14px;
  padding: 14px;
  background: #020617;
  color: #f1f5f9; /* BRIGHT text */
  border: 1px solid #64748b;
  border-radius: 8px;
  resize: vertical;
}

textarea::placeholder {
  color: #cbd5f5; /* brighter placeholder */
  line-height: 1.6;
}

.buttons {
  margin-top: 12px;
  display: flex;
  gap: 10px;
}

button {
  background: #38bdf8;
  color: #020617;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}

button:hover {
  opacity: 0.9;
}
</style>
